if (!customElements.get('category-tabs')) {
  customElements.define(
    'category-tabs',
    class CategoryTabs extends HTMLElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced = theme.config.motionReduced;

        this.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        this.syncActiveFromUrl();
      }

      syncActiveFromUrl() {
        const path = window.location.pathname.replace(/\/+$/, '');
        const segments = path.split('/').filter(Boolean);
        const urlTag = (segments.length >= 3 && segments[0] === 'collections') ? segments[2].toLowerCase() : '';
        let matched = false;
        this.querySelectorAll('a.category-tabs__item').forEach(link => {
          const tag = (link.dataset.tag || '').toLowerCase();
          const isActive = tag !== '' && tag === urlTag;
          if (isActive) matched = true;
          link.classList.toggle('is-active', isActive);
          link.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        if (!matched && urlTag === '') {
          const allLink = this.querySelector('a.category-tabs__item[data-tag=""]');
          if (allLink) {
            allLink.classList.add('is-active');
            allLink.setAttribute('aria-selected', 'true');
          }
        }
        this.updateDescription(urlTag);
      }

      updateDescription(tag) {
        const activeTag = (tag || '').toLowerCase();
        this.querySelectorAll('.category-tabs__description').forEach(desc => {
          const descTag = (desc.dataset.tag || '').toLowerCase();
          desc.toggleAttribute('hidden', descTag !== activeTag);
        });
      }

      getSectionId() {
        return this.dataset.sectionId;
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 30 : 50,
          duration: this.motionReduced ? 0.001 : (this.isMobile ? 0.3 : 0.5),
          staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
        };
      }

      getVisibleItems(items) {
        const viewportHeight = window.innerHeight + window.scrollY;
        const visible = [];
        const invisible = [];

        items.forEach(item => {
          const rect = item.getBoundingClientRect();
          const isVisible = rect.top < viewportHeight && rect.bottom > 0;
          if (isVisible) visible.push(item);
          else invisible.push(item);
        });

        return { visible, invisible };
      }

      onClick(event) {
        const link = event.target.closest('a.category-tabs__item');
        if (!link) return;
        if (link.classList.contains('is-active')) {
          event.preventDefault();
          return;
        }
        event.preventDefault();

        this.setActiveLink(link);

        const fetchUrl = new URL(link.href);
        fetchUrl.searchParams.set('section_id', this.getSectionId());
        this.renderSection(fetchUrl.toString());

        const cleanUrl = new URL(link.href);
        cleanUrl.searchParams.delete('section_id');
        history.pushState({}, '', cleanUrl.toString());
      }

      setActiveLink(activeLink) {
        this.querySelectorAll('a.category-tabs__item').forEach(a => {
          const isActive = a === activeLink;
          a.classList.toggle('is-active', isActive);
          a.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        this.updateDescription(activeLink.dataset.tag || '');
      }

      renderSection(url) {
        if (this.cachedMap.has(url)) {
          this.renderSectionFromCache(url);
        } else {
          this.renderSectionFromFetch(url);
        }
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.beforeRenderSection();
        const start = performance.now();

        fetch(url, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            const execution = performance.now() - start;
            setTimeout(() => {
              this.applyResponse(responseText);
              this.cachedMap.set(url, responseText);
              this.afterRenderSection();
            }, execution > 500 ? 0 : 500);
          })
          .catch(error => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      renderSectionFromCache(url) {
        this.beforeRenderSection();
        setTimeout(() => {
          this.applyResponse(this.cachedMap.get(url));
          this.afterRenderSection();
        }, 500);
      }

      applyResponse(responseText) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this._updateSection(parsedHTML, 'ProductGridContainer', (newContent) => {
          newContent.querySelector('motion-list')?.setAttribute('motion-reduced', '');
        });
        this._updateSection(parsedHTML, 'ProductCount');

        if (typeof theme !== 'undefined' && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText });
        }
      }

      _updateSection(parsedHTML, sectionId, modifier = null) {
        const container = document.getElementById(sectionId);
        if (!container) return false;
        const newContent = parsedHTML.getElementById(sectionId);
        if (!newContent) return false;
        modifier?.(newContent, container);
        container.innerHTML = newContent.innerHTML;
        return true;
      }

      beforeRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;
        const items = container.querySelectorAll('.product-card');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);

        Motion.timeline([
          [visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration, delay: Motion.stagger(staggerDelay) }],
          [inVisibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration }],
          [container, { y: distance, opacity: 0 }, { duration, easing: 'linear' }]
        ]);
        items.forEach(item => item.style.removeProperty('transform'));

        let headerHeight = 0;
        if (!theme.config.mqlSmall && document.querySelector('header.header')) {
          headerHeight = Math.round(document.querySelector('header.header').clientHeight);
        }
        setTimeout(() => {
          const target = document.querySelector('.collection');
          if (!target) return;
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) - headerHeight,
            behavior: theme.config.motionReduced ? 'auto' : 'smooth'
          });
        }, 100);
      }

      afterRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;
        const items = container.querySelectorAll('.product-card');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);

        Motion.timeline([
          [container, { y: [distance, 0], opacity: [0, 1] }, { duration, easing: 'linear' }],
          [visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration, delay: Motion.stagger(staggerDelay) }],
          [inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration }]
        ]);
        items.forEach(item => item.style.removeProperty('transform'));

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }
    }
  );
}
